import { FormEvent, KeyboardEvent, useCallback, useRef, useState } from 'react';

import {
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { Link } from 'react-router-dom';

import RoleEditor from './RoleEditor';
import { getInviteErrorMessage } from './utils/invites';
import useApp from '../../hooks/useApp';
import useTeam from '../../hooks/useTeam';
import Routes from '../../lib/routes';
import { getSDKClient } from '../../lib/sdk';
import { isValidEmail } from '../../utils/string';

export default function InviteTeamMemberV2(): JSX.Element {
  const { app } = useApp();
  const { team } = useTeam();
  const { enqueueSnackbar } = useSnackbar();
  const [emailInput, setEmailInput] = useState<string>('');
  const [committedEmail, setCommittedEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);
  const isSendingRef = useRef(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const commitEmailInput = (): string => {
    const trimmed = emailInput.trim();
    if (committedEmail !== '') {
      setEmailError('Only one email can be invited at a time.');
      return '';
    }
    if (!isValidEmail(trimmed)) {
      setEmailError('Enter a valid email address.');
      return '';
    }
    setCommittedEmail(trimmed);
    setEmailInput('');
    setEmailError('');
    return trimmed;
  };

  const removeCommittedEmail = () => {
    setCommittedEmail('');
    setEmailError('');
  };

  const handleEmailKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Backspace' && emailInput === '' && committedEmail !== '') {
      removeCommittedEmail();
      return;
    }
    // Enter adds the email instead of submitting the form
    if (e.key === 'Enter') {
      e.preventDefault();
    }
    if ((e.key === 'Enter' || e.key === 'Tab') && emailInput.trim() !== '') {
      commitEmailInput();
    }
  };

  const inviteTeamMember = useCallback(
    async (inviteeEmail: string) => {
      if (team.team_id === '') {
        return;
      }
      if (isSendingRef.current) {
        return;
      }

      const { env, tokens } = app;
      const currentToken = tokens.get(env) ?? '';
      const sdk = getSDKClient(env, currentToken);
      isSendingRef.current = true;
      setIsSending(true);
      try {
        await sdk.createTeamInvite(team.team_id, inviteeEmail, selectedRoles);
        enqueueSnackbar(`Invite sent to ${inviteeEmail}`, { variant: 'success' });
        setCommittedEmail('');
        setSelectedRoles([]);
      } catch (err) {
        enqueueSnackbar(getInviteErrorMessage(err), { variant: 'error' });
      } finally {
        isSendingRef.current = false;
        setIsSending(false);
      }
    },
    [app, team, selectedRoles, enqueueSnackbar],
  );

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    // prevent form from needlessly refreshing page
    e.preventDefault();
    // send the committed email if nothing is typed, otherwise try to commit the typed email and send it
    if (emailInput.trim() === '' && committedEmail !== '') {
      inviteTeamMember(committedEmail);
      return;
    }
    const typedEmail = commitEmailInput();
    if (typedEmail !== '') {
      inviteTeamMember(typedEmail);
    }
  };

  const isDisabled = isSending || team.team_id === '';

  const emailField = (
    <TextField
      id="email"
      variant="outlined"
      type="email"
      fullWidth
      label="Email address"
      value={emailInput}
      onChange={(e) => {
        setEmailInput(e.target.value);
        setEmailError('');
      }}
      onKeyDown={handleEmailKeyDown}
      error={emailError !== ''}
      helperText={emailError !== '' ? emailError : 'We will email them a link to join this team.'}
      autoComplete="off"
      disabled={isDisabled}
      // keep the label above the pill, since the pill isn't part of the input value
      InputLabelProps={{ shrink: committedEmail !== '' ? true : undefined }}
      InputProps={{
        startAdornment:
          committedEmail !== '' ? (
            <Chip
              label={committedEmail}
              color="primary"
              size="small"
              sx={{ mr: 1 }}
              onDelete={isDisabled ? undefined : removeCommittedEmail}
            />
          ) : undefined,
      }}
    />
  );

  const roles = (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">
          Roles{' '}
          <Box component="span" color="text.secondary">
            (optional)
          </Box>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Leave empty to invite as a Basic User, who can read non-sensitive information only.
        </Typography>
      </Box>
      <RoleEditor selectedRoles={selectedRoles} onSelectedRolesChange={setSelectedRoles} />
    </Stack>
  );

  const cancelButton = (
    <Button
      variant="outlined"
      component={Link}
      to={Routes.Teams.MembersV2}
      draggable={false}
      fullWidth={isMobile}
      disabled={isSending}
    >
      Cancel
    </Button>
  );

  const sendButton = (
    <Button
      variant="contained"
      type="submit"
      fullWidth={isMobile}
      disabled={isDisabled || (committedEmail === '' && emailInput.trim() === '')}
    >
      Send invite
    </Button>
  );

  if (isMobile) {
    return (
      <Dialog
        fullScreen
        open
        // use our own email validation instead of the browser's
        PaperProps={{ component: 'form', noValidate: true, onSubmit: handleSubmit }}
      >
        <DialogTitle component="div">
          Invite member
          <Typography variant="subtitle2" color="text.secondary">
            to <strong>{team.team_name}</strong>
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {emailField}
          <Box sx={{ pt: 2 }}>{roles}</Box>
        </DialogContent>
        <DialogActions disableSpacing sx={{ gap: 2, p: 2 }}>
          {cancelButton}
          {sendButton}
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ flexGrow: 1, p: 4 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Box>
          <Typography variant="h1" component="h1" gutterBottom>
            Invite member
          </Typography>
          <Typography color="text.secondary">
            to <strong>{team.team_name}</strong>
          </Typography>
        </Box>
        <Paper
          variant="outlined"
          component="form"
          // use our own email validation instead of the browser's
          noValidate
          onSubmit={handleSubmit}
        >
          <Stack divider={<Divider />}>
            <Box sx={{ p: 4 }}>{emailField}</Box>
            <Box sx={{ p: 4 }}>{roles}</Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, px: 4, py: 2, bgcolor: 'grey.50' }}>
              {cancelButton}
              {sendButton}
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}
